// src/App.jsx
// 主应用组件 - 管理路由和全局状态
// 访问地址: http://localhost:3000

import React, { useState, useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import Indexmain from "./components/Indexmain";
import { isAuthenticated, clearAuth } from "./utils/auth";
import "./App.css";

// Lazy load components
const LoginForm = lazy(() => import("./components/LoginForm"));
const RegisterForm = lazy(() => import("./components/RegisterForm"));
const Fengshui = lazy(() => import("./components/fengshui/Fengshui"));
const Zhishiku = lazy(() => import("./components/knowledges/Zhishiku"));
const HorseYearGuide = lazy(() => import("./components/knowledges/horseyear"));
const HorseFengshui = lazy(() => import("./components/knowledges/horsefengshui/horse_fengshui"));
const RatYearGuide = lazy(() => import("./components/knowledges/rat"));
const OxYearGuide = lazy(() => import("./components/knowledges/ox"));
const TigerYearGuide = lazy(() => import("./components/knowledges/tiger"));
const RabbitYearGuide = lazy(() => import("./components/knowledges/rabbit"));
const DragonYearGuide = lazy(() => import("./components/knowledges/dragon"));
const SnakeYearGuide = lazy(() => import("./components/knowledges/snake"));
const HorseYearGuideDetail = lazy(() => import("./components/knowledges/horse"));
const GoatYearGuide = lazy(() => import("./components/knowledges/goat"));
const MonkeyYearGuide = lazy(() => import("./components/knowledges/monkey"));
const RoosterYearGuide = lazy(() => import("./components/knowledges/rooster"));
const DogYearGuide = lazy(() => import("./components/knowledges/dog"));
const PigYearGuide = lazy(() => import("./components/knowledges/pig"));
const Fufeiyuyue = lazy(() => import("./components/yuyue/Fufeiyuyue"));
const LunarCalendar = lazy(() => import("./components/LunarCalendar"));
const About = lazy(() => import("./components/about"));
const DateInputInterface = lazy(() => import("./components/DateInputInterface"));
const Purchase = lazy(() => import("./components/purchase/Purchase"));
const UserInfo = lazy(() => import("./components/userAuth/Userinfo"));
const UserSubscriptions = lazy(() => import("./components/userAuth/UserSubscriptions"));
const UserPublications = lazy(() => import("./components/userAuth/UserPublications"));
const MyFollows = lazy(() => import("./components/userAuth/MyFollows"));
const UserProfile = lazy(() => import("./components/userAuth/UserProfile"));
const UserGualis = lazy(() => import("./components/userAuth/Usergualis"));
const Userfavorites = lazy(() => import("./components/userAuth/Userfavorites"));
const BaziHistory = lazy(() => import("./components/bazi/BaziHistory"));
const Bazi = lazy(() => import("./components/bazi/Bazi"));
const PortalIndex = lazy(() => import("./components/portal_index"));
const App_jifen = lazy(() => import("./App_jifen"));

function App() {
  // ==================== 状态管理 ====================
  // 当前视图：'calculator' | 'login' | 'register' | 'saved'
  const [currentView, setCurrentView] = useState("calculator");

  // 将 setCurrentView 暴露到全局，供 LocationWatcher 在最小改动场景下调用
  useEffect(() => {
    window.__app_set_current_view = setCurrentView;
    return () => {
      try {
        delete window.__app_set_current_view;
      } catch (e) {
        window.__app_set_current_view = undefined;
      }
    };
  }, [setCurrentView]);

  // 用户登录状态
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // 强制刷新计数器（用于重新渲染）
  const [refreshKey, setRefreshKey] = useState(0);

  // ==================== 检查登录状态 ====================
  const checkLoginStatus = () => {
    const loggedIn = isAuthenticated();
    setIsLoggedIn(loggedIn);
    console.log("🔐 登录状态检查:", loggedIn ? "已登录" : "未登录");
    return loggedIn;
  };

  // ==================== 初始化 ====================
  useEffect(() => {
    // 初始检查登录状态
    checkLoginStatus();

    // 监听存储变化（多标签页同步）
    const handleStorageChange = (e) => {
      if (e.key === "access_token") {
        console.log("🔄 检测到token变化");
        checkLoginStatus();
        setRefreshKey((prev) => prev + 1);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // ==================== 登录成功处理 ====================
  const handleLoginSuccess = (user) => {
    console.log("✅ 登录成功:", user);
    setIsLoggedIn(true);
    setCurrentView("calculator");

    // 显示欢迎消息
    if (user?.nickname) {
      setTimeout(() => {
        alert(`欢迎回来，${user.nickname}！`);
      }, 100);
    }
  };

  // ==================== 注册成功处理 ====================
  const handleRegisterSuccess = (user) => {
    console.log("✅ 注册成功:", user);
    // 注册成功后跳转到登录页
    setTimeout(() => {
      setCurrentView("login");
    }, 3000);
  };

  // ==================== 退出登录处理 ====================
  const handleLogout = () => {
    console.log("🚪 执行退出登录...");

    if (!window.confirm("确定要退出登录吗？")) {
      console.log("❌ 用户取消退出");
      return;
    }

    try {
      // 清除认证信息
      clearAuth();

      // 更新状态
      setIsLoggedIn(false);
      setCurrentView("calculator");
      setRefreshKey((prev) => prev + 1); // 强制刷新

      console.log("✅ 退出登录成功");

      // 显示提示消息
      setTimeout(() => {
        alert("已成功退出登录");
      }, 100);
    } catch (error) {
      console.error("❌ 退出登录失败:", error);
      alert("退出登录时出现错误");
    }
  };

  // 点击“六爻排盘”：在新标签页打开并通过 sessionStorage 传递登录状态/token
  const openLiuyaoPage = (e) => {
    e && e.preventDefault && e.preventDefault();
    const payload = {
      isLoggedIn,
      access_token: localStorage.getItem("access_token") || null,
      ts: Date.now(),
    };
    const key = `liuyao_payload_${Date.now()}`;
    try {
      sessionStorage.setItem(key, JSON.stringify(payload));
      const url = `/liuyao?stateKey=${encodeURIComponent(key)}`;
      const newWin = window.open(url, "_blank");
      if (!newWin) {
        window.location.href = url;
      }
    } catch (err) {
      window.location.href = `/liuyao`;
    }
  };

  // 点击“八字排盘”：在新标签页打开并通过 sessionStorage 传递登录状态/token
  const openBaziPage = (e) => {
    e && e.preventDefault && e.preventDefault();
    const payload = {
      isLoggedIn,
      access_token: localStorage.getItem("access_token") || null,
      ts: Date.now(),
    };
    const key = `bazi_payload_${Date.now()}`;
    try {
      sessionStorage.setItem(key, JSON.stringify(payload));
      const url = `/bazi?stateKey=${encodeURIComponent(key)}`;
      const newWin = window.open(url, "_blank");
      if (!newWin) {
        window.location.href = url;
      }
    } catch (err) {
      window.location.href = `/bazi`;
    }
  };
 
  // 点击“八宅风水”：在新标签页打开并传递登录状态
  const openFengshuiPage = (e) => {
    e && e.preventDefault && e.preventDefault();
    const payload = {
      isLoggedIn,
      access_token: localStorage.getItem("access_token") || null,
      ts: Date.now(),
    };
    const key = `fengshui_payload_${Date.now()}`;
    try {
      sessionStorage.setItem(key, JSON.stringify(payload));
      const url = `/fengshui?stateKey=${encodeURIComponent(key)}`;
      const newWin = window.open(url, "_blank");
      if (!newWin) {
        window.location.href = url;
      }
    } catch (err) {
      window.location.href = `/fengshui`;
    }
  };

  // 点击“奇门遁甲”：在新标签页打开并传递登录状态
  const openQimenPage = (e) => {
    e && e.preventDefault && e.preventDefault();
    const payload = {
      isLoggedIn,
      access_token: localStorage.getItem("access_token") || null,
      ts: Date.now(),
    };
    const key = `qimen_payload_${Date.now()}`;
    try {
      sessionStorage.setItem(key, JSON.stringify(payload));
      const url = `/qimen?stateKey=${encodeURIComponent(key)}`;
      const newWin = window.open(url, "_blank");
      if (!newWin) {
        window.location.href = url;
      }
    } catch (err) {
      window.location.href = `/qimen/index`;
    }
  };

  // 点击“易学知识库”：在新标签页打开并传递登录状态
  const openZhishikuPage = (e) => {
    e && e.preventDefault && e.preventDefault();
    const payload = {
      isLoggedIn,
      access_token: localStorage.getItem("access_token") || null,
      ts: Date.now(),
    };
    const key = `zhishiku_payload_${Date.now()}`;
    try {
      sessionStorage.setItem(key, JSON.stringify(payload));
      // 注意：路由前缀已经变成 /zhishiku
      const url = `/zhishiku?stateKey=${encodeURIComponent(key)}`;
      const newWin = window.open(url, "_blank");
      if (!newWin) {
        window.location.href = url;
      }
    } catch (err) {
      window.location.href = `/zhishiku`;
    }
  };

  // ==================== 视图切换 ====================
  const renderView = () => {
    switch (currentView) {
      case "login":
        return (
          <LoginForm
            onLoginSuccess={handleLoginSuccess}
            onSwitchToRegister={() => setCurrentView("register")}
          />
        );

      case "register":
        return (
          <RegisterForm
            onRegisterSuccess={handleRegisterSuccess}
            onSwitchToLogin={() => setCurrentView("login")}
          />
        );
      
      case "userinfo":
        return <UserInfo />;

      case "saved":
        // 检查是否登录
        if (!isLoggedIn) {
          return (
            <div className="max-w-md mx-auto p-6 mt-20">
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-6 py-4 rounded-lg text-center">
                <p className="font-semibold mb-2">⚠️ 需要登录</p>
                <p className="mb-4">请先登录后查看保存的命盘</p>
                <button
                  onClick={() => setCurrentView("login")}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
                >
                  去登录
                </button>
              </div>
            </div>
          );
        }
        return (
          <BaziHistory
            currentView={currentView}
            setCurrentView={setCurrentView}
            isLoggedIn={isLoggedIn}
            onLogout={handleLogout}
          />        );

      case "calculator":
      default:
        return <Bazi />;
    }
  };

  // ==================== 渲染主应用 ====================
  return (
    <HelmetProvider>
      <div className="min-h-screen bg-gray-50">
        {/* 主内容区域 */}
        <main>
          <Suspense fallback={<div className="flex justify-center items-center min-h-screen text-gray-500">加载中...</div>}>
            <Routes>
              <Route path="/" element={<Indexmain />} />
              <Route path="/fengshui" element={<Fengshui />} />  
              <Route path="/fufeiyuyue" element={<Fufeiyuyue />} />
              <Route path="/_zhishiku" element={<Zhishiku />} />
              <Route path="/horseyear" element={<HorseYearGuide />} />
              <Route path="/horse-fengshui" element={<HorseFengshui />} />
              <Route path="/rat-year" element={<RatYearGuide />} />     
              <Route path="/ox-year" element={<OxYearGuide />} />
              <Route path="/tiger-year" element={<TigerYearGuide />} />
              <Route path="/rabbit-year" element={<RabbitYearGuide />} />
              <Route path="/dragon-year" element={<DragonYearGuide />} />
              <Route path="/snake-year" element={<SnakeYearGuide />} />
              <Route path="/horse-year" element={<HorseYearGuideDetail />} />
              <Route path="/goat-year" element={<GoatYearGuide />} />
              <Route path="/monkey-year" element={<MonkeyYearGuide />} />
              <Route path="/rooster-year" element={<RoosterYearGuide />} />
              <Route path="/dog-year" element={<DogYearGuide />} />
              <Route path="/pig-year" element={<PigYearGuide />} />
              <Route path="/login" element={<LoginForm />} />
              <Route path="/register" element={<RegisterForm />} />
              <Route path="/lunarcalendar" element={<LunarCalendar />} />
              <Route path="/about" element={<About />} />
              <Route path="/date-input-help" element={<DateInputInterface />} />
              <Route path="/purchase" element={<Purchase />} />
              <Route path="/yunying/userinfo" element={<UserInfo />} />
              <Route path="/user/subscriptions" element={<UserSubscriptions />} />
              <Route path="/user/publications" element={<UserPublications />} />
              <Route path="/user/follows" element={<MyFollows />} />
              <Route path="/user/gualis" element={<UserGualis />} />
              <Route path="/user/favorites" element={<Userfavorites />} />
              <Route path="/user/profile/:userId" element={<UserProfile />} />
              <Route path="/portal" element={<PortalIndex />} />
              <Route path="/jifen/*" element={<App_jifen />} />
             
              {/* ...other routes... */}
            </Routes>
          </Suspense>
        </main>
      </div>
    </HelmetProvider>
  );
}

export default App;


